// src/lib/quotePermissions.js
// v3.5.67: per-user quote-type permissions.
//
// Each user has profiles.allowed_quote_types text[] containing zero or more
// of the five canonical quote-type keys. Default is all five so existing
// users keep full access until an admin restricts them.
//
// Enforcement layers:
//   1. UI — QuotesPage filters the New Quote dropdown; quote pages gate on
//      load and force view-only mode if the user lacks permission for an
//      existing quote of that type.
//   2. Database — Postgres RLS policies on the `quotes` table enforce the
//      same rule server-side so a determined user can't bypass via direct
//      Supabase API calls. RLS reads the user's profile and rejects inserts
//      whose quote_type isn't in their allowed_quote_types array.
//
// Both layers must agree. If you add a new quote type, add it here AND to
// the RLS policy + Admin UI checkboxes.

export const QUOTE_TYPES = [
  { key: 'managed_it', label: 'Managed IT',          path: '/',              description: 'Standalone IT services per package' },
  { key: 'voice',      label: 'Hosted Voice',        path: '/voice/new',     description: 'Hosted seats, hybrid, or SIP trunking' },
  { key: 'bundle',     label: 'Bundle (IT + Voice)', path: '/bundle/new',    description: 'Combined IT + Voice with bundle discount' },
  { key: 'multisite',  label: 'Multi-Site IT',       path: '/multisite/new', description: 'IT services across multiple locations' },
  { key: 'flexit',     label: 'FlexIT On-Demand',    path: '/flexIT/new',    description: 'On-demand T&M block hours or pay-as-you-go' },
];

export const ALL_QUOTE_TYPE_KEYS = QUOTE_TYPES.map(t => t.key);

// Resolves a quote row's data shape to its canonical type key. Quote pages
// match against this when gating "is this user allowed to edit this saved
// quote type?".
//
// v3.5.70: dropped vestigial `quote.quote_type === 'flexIT'` check. The
// quotes table had no quote_type column at the time — the field was always
// undefined, always evaluated false, and always fell through to the
// package_name branches. Now matches the corrected v3.5.67 SQL helper which
// is also package_name-only.
//
// v3.5.91.1: the quote_type column NOW EXISTS (added in v3.5.91.0) and is
// the canonical source of truth. Prefer it; fall back to prefix-matching
// for defense in depth during the dual-write window.
//
// v3.5.91.2: prefix-matching fallback removed. Stored package_name no
// longer carries a type prefix; quote_type column with its CHECK
// constraint is the sole source of truth. The 'managed_it' default in
// the rare null-row case keeps the original "catch-all for IT" semantics.
export function quoteRowToType(quote) {
  if (!quote) return null;
  return quote.quote_type || 'managed_it';
}

// Check if a user's profile permits creating/editing a given quote type.
// Returns true if:
//   - profile is null/undefined (loading state — fail open so the page can
//     render; the actual save will fail at RLS if the user doesn't have
//     permission, which is the safe-by-default position)
//   - profile.role === 'admin' (admins have all permissions implicitly)
//   - profile.allowed_quote_types is missing (legacy account before the
//     v3.5.67 migration — DB default fills it as all-five, but in case
//     any row slipped through, fail open to all-five)
//   - profile.allowed_quote_types contains the requested type
export function userCanCreateQuoteType(profile, type) {
  if (!profile) return true;                 // loading state — defer to RLS
  if (profile.role === 'admin') return true; // admins always allowed
  const allowed = profile.allowed_quote_types;
  if (!Array.isArray(allowed)) return true;  // legacy / unmigrated row
  return allowed.includes(type);
}

// Returns the list of QUOTE_TYPES entries the user is permitted to create.
// Used by QuotesPage to filter the New Quote dropdown. Empty array means
// the user has no quote-creation permissions and the button itself should
// be disabled with an explanation tooltip.
export function getAllowedQuoteTypes(profile) {
  return QUOTE_TYPES.filter(t => userCanCreateQuoteType(profile, t.key));
}
